import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import {
  Alert,
  Box,
  Chip,
  Divider,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type { DecisionEngineJourneyDto } from '../../api/admin';
import type { ApplicationTabKey } from '../../lib/applicationDetailTabStatus';
import {
  buildPdlEligibilityExplanation,
  type PdlEligibilityExplanation,
} from '../../lib/pdlEligibilityExplainer';
import { buildPdlMechanismSummary } from '../../lib/pdlAdjudicatorCopy';
import type { PdlDecisionEvidence } from '../../lib/pdlDecisionEvidence';
import { PDL_AMOUNT_STRIP, PDL_BRIEF_SUBTITLE, PDL_BRIEF_TITLE } from '../../lib/pdlAdjudicatorUiCopy';
import { PdlDecisionEvidenceStrip } from './PdlDecisionEvidenceStrip';
import {
  buildPdlCapacityWaterfall,
  parsePdlCurrency,
} from '../../lib/pdlCapacityWaterfall';
import { buildPdlElodElevatedNotes } from '../../lib/pdlDecisionPanelRows';
import { PDL_INNER_RADIUS, PDL_CHIP_SX } from '../../lib/pdlDecisionChrome';
import {
  AmbientGlassShell,
  PDL_DECISION_SUMMARY_GLOW,
} from '../employerReview/AmbientGlassShell';
import { ApplicationSectionHeader } from '../application/applicationSectionChrome';
import { isPdlDecisionReviewPathTourDismissed } from '../../lib/pdlDecision/pdlDecisionTourStorage';
import { usePdlDecisionTourOptional } from './guidedTour/PdlDecisionTourContext';
import { PdlCapacityWaterfallVisual } from './PdlCapacityWaterfallVisual';

type Props = {
  decisionEngine?: DecisionEngineJourneyDto | null;
  applicationDeclineReason?: string | null;
  /** Vendor / journey decline code when the red decline block is suppressed for PDL. */
  declineCode?: string | null;
  evidence?: PdlDecisionEvidence | null;
  evidenceLoading?: boolean;
  compact?: boolean;
  onNavigate?: (key: ApplicationTabKey) => void;
};

function decisionChipColor(decision: string): 'success' | 'error' | 'warning' | 'default' {
  if (decision === 'APPROVE') return 'success';
  if (decision === 'DECLINE') return 'error';
  if (decision === 'REFER') return 'warning';
  return 'default';
}

function AmountComparisonRow({
  label,
  value,
  emphasize,
  tone,
}: {
  label: string;
  value: string;
  emphasize?: boolean;
  tone?: 'default' | 'success' | 'muted';
}) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 800,
          display: 'block',
          letterSpacing: '0.02em',
          color:
            tone === 'success'
              ? 'success.dark'
              : tone === 'muted'
                ? 'text.secondary'
                : 'text.primary',
        }}
      >
        {label}
      </Typography>
      <Typography
        variant={emphasize ? 'h6' : 'body1'}
        sx={{
          fontWeight: emphasize ? 800 : 700,
          lineHeight: 1.25,
          color:
            tone === 'success'
              ? 'success.main'
              : tone === 'muted'
                ? 'text.secondary'
                : 'text.primary',
          fontVariantNumeric: 'tabular-nums',
          wordBreak: 'break-word',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function mergeNotes(elodNotes: string[], softSignals: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const note of [...elodNotes, ...softSignals]) {
    const key = note.trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(note);
  }
  return out;
}

export function PdlEligibilityBriefContent({
  explanation,
  elodNotes = [],
  mechanismSummary = null,
  evidence = null,
  evidenceLoading = false,
  declineCode,
  compact = false,
  onNavigate,
}: {
  explanation: PdlEligibilityExplanation;
  elodNotes?: string[];
  mechanismSummary?: ReturnType<typeof buildPdlMechanismSummary>;
  evidence?: PdlDecisionEvidence | null;
  evidenceLoading?: boolean;
  declineCode?: string | null;
  compact?: boolean;
  onNavigate?: (key: ApplicationTabKey) => void;
}) {
  const theme = useTheme();
  const notes = mergeNotes(elodNotes, explanation.activeSoftSignals);
  const showAmountStrip =
    explanation.amountComparison.requested || explanation.amountComparison.approved;
  const waterfall = buildPdlCapacityWaterfall({
    mechanism: mechanismSummary,
    requested: parsePdlCurrency(explanation.amountComparison.requested),
    approved: parsePdlCurrency(explanation.amountComparison.approved),
    why: null,
  });
  const showEvidenceNotes = !evidence && notes.length > 0;

  const showEvidenceBlock = evidenceLoading || Boolean(evidence?.income || evidence?.debt);

  return (
    <Stack spacing={compact ? 1.25 : 1.5}>
      {showAmountStrip || showEvidenceBlock ? (
        <Box
          sx={{
            borderRadius: PDL_INNER_RADIUS,
            border: '1px solid',
            borderColor: alpha(theme.palette.primary.main, 0.18),
            bgcolor: alpha(theme.palette.primary.main, 0.03),
            overflow: 'hidden',
          }}
          data-testid="pdl-adjudicator-offer-evidence"
        >
          {showAmountStrip ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr' },
                gap: 1.25,
                p: 1.5,
              }}
              data-testid="pdl-adjudicator-amount-comparison"
            >
              {explanation.amountComparison.requested ? (
                <AmountComparisonRow
                  label={PDL_AMOUNT_STRIP.requested}
                  value={explanation.amountComparison.requested}
                />
              ) : null}
              {explanation.amountComparison.approved ? (
                <AmountComparisonRow
                  label={PDL_AMOUNT_STRIP.approved}
                  value={explanation.amountComparison.approved}
                  emphasize
                  tone="success"
                />
              ) : null}
            </Box>
          ) : null}
          {showEvidenceBlock ? (
            <PdlDecisionEvidenceStrip
              evidence={evidence}
              loading={evidenceLoading}
              onNavigate={onNavigate}
              embedded
            />
          ) : null}
        </Box>
      ) : null}

      {explanation.decision === 'DECLINE' && declineCode ? (
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip size="small" color="error" variant="outlined" label={declineCode} sx={{ ...PDL_CHIP_SX, fontWeight: 800 }} />
        </Stack>
      ) : null}

      {waterfall ? <PdlCapacityWaterfallVisual model={waterfall} /> : null}

      {!waterfall && explanation.amountComparison.why ? (
        <Alert
          severity={explanation.decision === 'DECLINE' ? 'error' : 'info'}
          icon={false}
          sx={{ py: 0.75, borderRadius: 1.5, '& .MuiAlert-message': { width: '100%' } }}
          data-testid="pdl-adjudicator-why"
        >
          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.45 }}>
            {explanation.amountComparison.why}
          </Typography>
        </Alert>
      ) : null}

      {explanation.decision === 'REFER' ? (
        <Alert
          severity="warning"
          icon={false}
          sx={{ py: 0.75, borderRadius: 1.5 }}
          data-testid="pdl-adjudicator-refer-action"
        >
          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.45 }}>
            Send to manual review — credit decision returned REFER.
            {onNavigate ? (
              <>
                {' '}
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  onClick={() => onNavigate('manual-review')}
                  sx={{ fontWeight: 700, verticalAlign: 'baseline' }}
                >
                  Open Manual review tab
                </Link>
              </>
            ) : null}
          </Typography>
        </Alert>
      ) : null}

      {showEvidenceNotes && notes.length > 0 ? (
        <Box data-testid="pdl-adjudicator-soft-signals">
          <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', display: 'block', mb: 0.5 }}>
            Notes — informational only
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {notes.map((item) => (
              <Chip
                key={item}
                size="small"
                variant="outlined"
                label={item}
                sx={PDL_CHIP_SX}
              />
            ))}
          </Stack>
        </Box>
      ) : null}
    </Stack>
  );
}

export function PdlAdjudicatorBrief({
  decisionEngine,
  applicationDeclineReason,
  declineCode,
  evidence = null,
  evidenceLoading = false,
  compact = false,
  onNavigate,
}: Props) {
  const tour = usePdlDecisionTourOptional();
  const explanation = buildPdlEligibilityExplanation(decisionEngine, applicationDeclineReason);
  if (!explanation) return null;

  const decisionColor = decisionChipColor(explanation.decision);
  const elodNotes = buildPdlElodElevatedNotes(decisionEngine?.pdlAffordability);
  const mechanismSummary = buildPdlMechanismSummary(
    decisionEngine?.pdlAffordability,
    decisionEngine?.maxEligibleAmount ?? decisionEngine?.approvedAmount,
  );
  const hasEvidence = Boolean(evidence?.income || evidence?.debt);

  return (
    <Box data-testid="pdl-adjudicator-brief" data-tour-target="pdl-adjudicator-brief">
      <AmbientGlassShell glow={PDL_DECISION_SUMMARY_GLOW} surface="summary">
        <Box sx={{ p: compact ? 1.25 : 1.5 }}>
          <ApplicationSectionHeader
            title={PDL_BRIEF_TITLE}
            subtitle={PDL_BRIEF_SUBTITLE}
            icon={<AccountBalanceWalletOutlinedIcon fontSize="small" />}
            iconColor={decisionColor === 'default' ? 'primary' : decisionColor}
            endAdornment={
              <Chip
                size="small"
                color={decisionColor}
                label={explanation.decision}
                sx={{ ...PDL_CHIP_SX, fontWeight: 800 }}
              />
            }
            mb={1.25}
          />
          <PdlEligibilityBriefContent
            explanation={explanation}
            elodNotes={elodNotes}
            mechanismSummary={mechanismSummary}
            evidence={evidence}
            evidenceLoading={evidenceLoading}
            declineCode={declineCode}
            compact={compact}
            onNavigate={onNavigate}
          />
          {tour ? (
            <>
              <Divider sx={{ my: 1.25 }} />
              <Link
                component="button"
                type="button"
                variant="body2"
                underline="hover"
                onClick={() => tour.startReviewPathTour({ hasEvidence })}
                data-tour-target="pdl-decision-take-tour"
                sx={{ fontWeight: 600, fontSize: '0.8125rem', alignSelf: 'flex-start' }}
              >
                {isPdlDecisionReviewPathTourDismissed()
                  ? 'Replay guided tour'
                  : 'Take guided tour'}
              </Link>
            </>
          ) : null}
        </Box>
      </AmbientGlassShell>
    </Box>
  );
}
